import { useEffect, useRef, useState } from "react"
import { landingCopy } from "../landingCopy"
import { timerClass } from "../lightTheme"
import { useLandingLanguage } from "../useLandingLanguage"

const startSeconds = 26
const endSeconds = 15
const tickMs = 1000

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function formatCountdown(seconds) {
  const whole = Math.max(0, seconds)
  const mins = String(Math.floor(whole / 60)).padStart(2, "0")
  const secs = String(whole % 60).padStart(2, "0")
  return `${mins}:${secs}`
}

function Countdown({ intercepted }) {
  const ref = useRef(null)
  const [seconds, setSeconds] = useState(startSeconds)
  const [active, setActive] = useState(false)
  const [done, setDone] = useState(false)
  const [reduced, setReduced] = useState(prefersReducedMotion)
  const showLabel = reduced || done

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const onChange = () => setReduced(query.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  useEffect(() => {
    const node = ref.current
    if (!node || reduced) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setActive(true)
      },
      { threshold: 0.6 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [reduced])

  useEffect(() => {
    if (!active || reduced) return undefined
    const startedAt = performance.now()
    const id = window.setInterval(() => {
      const elapsed = Math.floor((performance.now() - startedAt) / tickMs)
      const next = Math.max(endSeconds, startSeconds - elapsed)
      setSeconds(next)
      if (next === endSeconds && elapsed > startSeconds - endSeconds) {
        setDone(true)
        window.clearInterval(id)
      }
    }, tickMs)
    return () => window.clearInterval(id)
  }, [active, reduced])

  return (
    <div ref={ref} className="grid">
      <p
        className={`${timerClass} col-start-1 row-start-1 tabular-nums transition-opacity duration-700 ease-in-out ${
          showLabel ? "opacity-0" : "opacity-100"
        } ${reduced ? "transition-none" : ""}`}
        aria-hidden="true"
      >
        {formatCountdown(seconds)}
      </p>
      <p
        className={`col-start-1 row-start-1 self-center whitespace-nowrap text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717] transition-opacity duration-700 ease-in-out ${
          showLabel ? "opacity-100" : "opacity-0"
        } ${reduced ? "transition-none" : ""}`}
        aria-hidden={!showLabel}
      >
        {intercepted}
      </p>
    </div>
  )
}

export default function SundayGapSection() {
  const language = useLandingLanguage()
  const copy = landingCopy(language, "sundayGap")
  const paragraphs = [copy.lead, copy.body]

  return (
    <section className="flex w-full flex-col items-center gap-16 py-16 text-center md:flex-row-reverse md:justify-center md:gap-20 md:py-24 md:text-left lg:w-[calc(100vw-3rem)] lg:gap-24 lg:py-0">
      <div className="flex justify-center md:justify-start">
        <Countdown intercepted={copy.intercepted} />
      </div>
      <div className="flex w-full max-w-[640px] flex-col items-center md:items-start">
        <div className="flex flex-col gap-3">
          <h2 className="text-balance text-[48px] font-bold leading-[56px] tracking-[-0.04em] text-[#171717] max-md:text-[32px] max-md:leading-10">
            {copy.title}
          </h2>
          <div className="flex flex-col gap-6">
            {paragraphs.map((paragraph) => (
              <p
                key={paragraph}
                className="text-balance text-[16px] font-normal leading-6 text-[#4D4D4D] md:text-[18px] md:leading-7 lg:text-[20px] lg:leading-8"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
